import { fetchQuery } from 'react-relay';
import type { GraphQLTaggedNode } from 'relay-runtime';
import { createAri } from '@atlassian/jira-platform-ari';
import type { cascadingSelectIssueTransitionIssueAdjustmentsQuery } from '@atlassian/jira-relay/src/__generated__/cascadingSelectIssueTransitionIssueAdjustmentsQuery.graphql';
import type { issueTypeQuery } from '@atlassian/jira-relay/src/__generated__/issueTypeQuery.graphql';
import type { multiSelectQuery } from '@atlassian/jira-relay/src/__generated__/multiSelectQuery.graphql';
import type { priorityIssueTransitionIssueAdjustmentsQuery } from '@atlassian/jira-relay/src/__generated__/priorityIssueTransitionIssueAdjustmentsQuery.graphql';
import type { resolutionQuery } from '@atlassian/jira-relay/src/__generated__/resolutionQuery.graphql';
import type { PublicToInternalPropertyTransformerContext } from '../../types';

// extend this type when adding support for new field
type QueryType =
	| resolutionQuery
	| multiSelectQuery
	| priorityIssueTransitionIssueAdjustmentsQuery
	| issueTypeQuery
	| cascadingSelectIssueTransitionIssueAdjustmentsQuery;

type FetchFieldOptionsProps = {
	optionARIs: string[];
	nodePropertyName: string;
	queryName: string;
	query: GraphQLTaggedNode;
	extraQueryVariables?: object;
} & PublicToInternalPropertyTransformerContext;

export const fetchFieldOptions = async <T extends QueryType>({
	tenantContext,
	viewContext,
	environment,
	fieldContext,
	optionARIs,
	nodePropertyName,
	queryName,
	query,
	extraQueryVariables = {},
}: FetchFieldOptionsProps) => {
	let response: T['response'] | undefined;

	try {
		response = await fetchQuery<T>(
			environment,
			query,
			{
				ari: createAri({
					resourceOwner: 'jira',
					cloudId: tenantContext.cloudId,
					resourceType: 'issuefieldvalue',
					resourceId: `${viewContext.issueId}/${fieldContext.fieldId}`,
				}),
				filterById: {
					optionIds: optionARIs,
					operation: 'ALLOW',
				},
				...extraQueryVariables,
			},
			{
				networkCacheConfig: { force: false },
				fetchPolicy: 'store-or-network',
			},
		).toPromise();

		if (
			!response ||
			!response.node ||
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			!response.node[nodePropertyName as keyof QueryType['response']['node']]
		) {
			throw new Error(`[${queryName}]: Property ${nodePropertyName} does not exist.`, {
				cause: `${queryName}_MISSING_FIELD_OPTIONS_PROPERTY`,
			});
		}
	} catch (err: unknown) {
		const error = err instanceof Error ? err : new Error();
		if (!error.cause) {
			error.cause = `${queryName}_FIELD_OPTION_REQUEST_FAILED`;
		}

		throw error;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return response.node[nodePropertyName as keyof QueryType['response']['node']];
};
